import { useEffect, useState } from 'react';
import { Search, Download, Plus, X } from 'lucide-react';
import { po, apiError } from '../api';

export default function PoSupplier() {
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(null);

  const load = () => {
    po.get('/suppliers').then((r) => setSupplier(r.data[0]));
    po.get('/products').then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveSupplier = async () => {
    setMsg('');
    try {
      const { id, ...fields } = supplier;
      fields.contacts = (supplier.contacts || []).filter((c) => c.email?.trim());
      await po.patch(`/suppliers/${id}`, fields);
      setMsg('Supplier saved.');
    } catch (e) { setMsg(apiError(e)); }
  };

  const setContact = (i, patch) => setSupplier((s) => {
    const contacts = [...(s.contacts || [])];
    contacts[i] = { ...contacts[i], ...patch };
    return { ...s, contacts };
  });
  const addContact = () => setSupplier((s) => ({ ...s, contacts: [...(s.contacts || []), { name: '', email: '' }] }));
  const removeContact = (i) => setSupplier((s) => ({ ...s, contacts: (s.contacts || []).filter((_, x) => x !== i) }));

  const saveProduct = async () => {
    setMsg('');
    try {
      const { id, supplier_id, currency, updated_at, ...fields } = edit;
      fields.unit_cost_exw = Number(fields.unit_cost_exw) || 0;
      const { data } = await po.patch(`/products/${id}`, fields);
      setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
      setEdit(null);
    } catch (e) { setMsg(apiError(e)); }
  };

  const toggleActive = async (p) => {
    try {
      const { data } = await po.patch(`/products/${p.id}`, { active: !p.active });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch (e) { alert(apiError(e)); }
  };

  const exportCsv = async () => {
    const { data } = await po.get('/products/export.csv', { responseType: 'blob' });
    const u = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = u; a.download = `po-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(u); }, 2000);
  };

  const shown = products.filter((p) => {
    if (cat !== 'all' && p.category !== cat) return false;
    const s = search.trim().toLowerCase();
    return !s || p.ref.toLowerCase().includes(s) || p.name.toLowerCase().includes(s);
  });

  return (
    <div className="p-6 md:p-8" data-testid="po-supplier-page">
      <h1 className="text-xl font-bold text-[#14233F] mb-1">Supplier &amp; Catalogue</h1>
      <p className="text-xs text-amber-600 font-semibold mb-5">Internal purchase costs (EXW) — never shared with the quotation module.</p>
      {msg && <p className="mb-4 text-sm bg-teal-50 border border-teal-200 text-[#0F7D70] rounded px-4 py-2.5" data-testid="po-supplier-msg">{msg}</p>}

      {supplier && (
        <div className="bg-white border border-slate-200 rounded p-5 mb-8 max-w-3xl" data-testid="po-supplier-form">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Supplier</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input value={supplier.name || ''} onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} placeholder="Name" className="border border-slate-300 rounded px-3 py-2 text-sm col-span-2" data-testid="po-sup-name" />
            <input value={supplier.country || ''} onChange={(e) => setSupplier({ ...supplier, country: e.target.value })} placeholder="Country" className="border border-slate-300 rounded px-3 py-2 text-sm" />
            <input value={supplier.address || ''} onChange={(e) => setSupplier({ ...supplier, address: e.target.value })} placeholder="Address" className="border border-slate-300 rounded px-3 py-2 text-sm col-span-2 md:col-span-3" />
            <input value={supplier.contact_name || ''} onChange={(e) => setSupplier({ ...supplier, contact_name: e.target.value })} placeholder="Contact name" className="border border-slate-300 rounded px-3 py-2 text-sm" />
            <input value={supplier.contact_email || ''} onChange={(e) => setSupplier({ ...supplier, contact_email: e.target.value })} placeholder="Contact email" className="border border-slate-300 rounded px-3 py-2 text-sm" />
            <input value={supplier.incoterm || ''} onChange={(e) => setSupplier({ ...supplier, incoterm: e.target.value })} placeholder="Default incoterm" className="border border-slate-300 rounded px-3 py-2 text-sm" />
            <input value={supplier.notes || ''} onChange={(e) => setSupplier({ ...supplier, notes: e.target.value })} placeholder="Notes" className="border border-slate-300 rounded px-3 py-2 text-sm col-span-2 md:col-span-3" />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Email contacts (send recipients)</p>
              <button onClick={addContact} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F7D70] hover:underline" data-testid="po-contact-add">
                <Plus className="h-3.5 w-3.5" /> Add contact
              </button>
            </div>
            {(supplier.contacts || []).map((c, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <input value={c.name || ''} onChange={(e) => setContact(i, { name: e.target.value })} placeholder="Name"
                  className="border border-slate-300 rounded px-3 py-2 text-sm w-56" data-testid={`po-contact-name-${i}`} />
                <input value={c.email || ''} onChange={(e) => setContact(i, { email: e.target.value })} placeholder="email@company.com" type="email"
                  className="border border-slate-300 rounded px-3 py-2 text-sm flex-1" data-testid={`po-contact-email-${i}`} />
                <button onClick={() => removeContact(i)} title="Remove" data-testid={`po-contact-rm-${i}`}>
                  <X className="h-4 w-4 text-slate-300 hover:text-red-600" />
                </button>
              </div>
            ))}
            {(supplier.contacts || []).length === 0 && <p className="text-xs text-slate-400">No contacts — add the people who should be able to receive POs.</p>}
          </div>
          <button onClick={saveSupplier} className="mt-3 bg-[#14233F] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="po-sup-save">Save supplier</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ref or name…"
            className="border border-slate-300 rounded pl-9 pr-3 py-2 text-sm w-64" data-testid="po-cat-search" />
        </div>
        {['all', 'gateway', 'sensor', 'accessory'].map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-3 py-1.5 text-xs font-medium rounded border capitalize ${cat === c ? 'bg-[#14233F] text-white border-[#14233F]' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid={`po-cat-${c}`}>
            {c} <span className="ml-1 mono text-[10px] opacity-60">{c === 'all' ? products.length : products.filter((p) => p.category === c).length}</span>
          </button>
        ))}
        <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-[#0F7D70] text-[#0F7D70] hover:bg-teal-50" data-testid="po-csv-export">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3 font-semibold">Ref</th>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-right px-4 py-3 font-semibold">Unit cost EXW €</th>
              <th className="text-center px-4 py-3 font-semibold">Active</th>
              <th className="text-right px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              edit?.id === p.id ? (
                <tr key={p.id} className="border-b border-slate-100 bg-teal-50/40">
                  <td className="px-4 py-2"><input value={edit.ref} onChange={(e) => setEdit({ ...edit, ref: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm w-36 mono" data-testid="po-edit-ref" /></td>
                  <td className="px-4 py-2"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm w-full" data-testid="po-edit-name" /></td>
                  <td className="px-4 py-2">
                    <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm">
                      {['gateway', 'sensor', 'accessory'].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right"><input type="number" step="0.0001" value={edit.unit_cost_exw} onChange={(e) => setEdit({ ...edit, unit_cost_exw: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm w-28 text-right mono" data-testid="po-edit-cost" /></td>
                  <td className="px-4 py-2 text-center"><input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={saveProduct} className="text-xs font-semibold text-[#0F7D70] hover:underline mr-3" data-testid="po-edit-save">Save</button>
                    <button onClick={() => setEdit(null)} className="text-xs text-slate-400 hover:underline">Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!p.active ? 'opacity-45' : ''}`}>
                  <td className="px-4 py-2.5 mono text-xs text-slate-600">{p.ref}</td>
                  <td className="px-4 py-2.5 text-slate-800">{p.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">{p.category}</td>
                  <td className="px-4 py-2.5 mono text-right">{Number(p.unit_cost_exw).toFixed(4)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <button onClick={() => toggleActive(p)} role="switch" aria-checked={!!p.active}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${p.active ? 'bg-[#0F7D70]' : 'bg-slate-300'}`}
                      data-testid={`po-active-${p.ref}`}>
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${p.active ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEdit({ ...p })} className="text-xs text-[#0F7D70] hover:underline" data-testid={`po-prod-edit-${p.ref}`}>Edit</button>
                  </td>
                </tr>
              )
            ))}
            {shown.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400 text-sm">No products match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
